import React, { MouseEvent, ReactText, FC } from 'react';
import { Link } from 'react-router-dom';
import _map from 'lodash/map';
import _isNil from 'lodash/isNil';
import { AppType } from '@blocs.common';
import { useTranslation } from '@blocs.i18n';
import Select from '@minecraft.select';
import { CoreTheme as theme } from '@minecraft.themes';
import Loader from '@minecraft.loader';
import { SelectOption } from '../../../shared/types';
import { CTAItemLabel, FolderNameLabel, FolderNameText } from '../../shared/styles';
import {
  Wrapper,
  SideBarItem,
  FolderInfo,
  SelectWrapper,
  StyledChooseTalentButton,
  StyledDirectionButton,
  StyledDirectionButtonGroup,
  StyledChevronLeftIcon,
  StyledGridViewIcon,
  FolderTalentCount,
  TalentList,
  StyledTalentCardLink,
  StyledTalentCard,
  LoaderWrapper,
} from './RecipientDetailViewSideBarStyles';

type Props = {
  isNextPageLoading: boolean;
  hasNextPage: boolean;
  appType?: AppType;
  currentTalentGuid: string;
  backToHomeLinkPath: string;
  gridViewLinkPath: string;
  folderName: string;
  talentCount: number;
  selectedFolder: string;
  folderOptions: SelectOption[];
  currentTalentPosition: number;
  prevTalentGuid: ReactText;
  nextTalentGuid: ReactText;
  talentList: any[];
  handleFolderChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleTalentChange: (event: MouseEvent<HTMLButtonElement>) => void;
  handleSeeAllTalentModalOpen: () => void;
  handleScroll: (event: MouseEvent<HTMLDivElement>) => void;
  className?: string;
  userName?: string;
  userNameModalToggle?: () => void;
  presentationGuid?: string;
  presentationId?: number;
  isShareSelectsPresentation?: boolean;
};

const RecipientDetailViewSideBar: FC<Props> = ({
  isNextPageLoading,
  hasNextPage,
  appType,
  currentTalentGuid,
  backToHomeLinkPath,
  gridViewLinkPath,
  folderName,
  talentCount,
  selectedFolder,
  folderOptions,
  currentTalentPosition,
  prevTalentGuid,
  nextTalentGuid,
  talentList,
  handleFolderChange,
  handleSeeAllTalentModalOpen,
  handleTalentChange,
  handleScroll,
  className,
  userName,
  userNameModalToggle,
  presentationId,
  presentationGuid,
  isShareSelectsPresentation,
}) => {
  const { t } = useTranslation();

  return (
    <Wrapper className={className} onScroll={handleScroll}>
      <SideBarItem>
        <Link to={backToHomeLinkPath}>
          <StyledChevronLeftIcon name="chevron-left-thick" color={theme.colors.primary.primary} />
          {t('common:button.backToHome')}
        </Link>
      </SideBarItem>
      <SideBarItem>
        <CTAItemLabel>{t('common:form.label.switchTo')}</CTAItemLabel>
        <Link to={gridViewLinkPath}>
          <StyledGridViewIcon name="view-card" color={theme.colors.primary.primary} />
          {t('common:button.gridView')}
        </Link>
      </SideBarItem>
      {folderOptions.length > 1 && (
        <SideBarItem>
          <CTAItemLabel>{t('common:form.label.chooseFolder')}</CTAItemLabel>
          <SelectWrapper>
            <Select defaultValue={selectedFolder} options={folderOptions} size="small" onChange={handleFolderChange} />
          </SelectWrapper>
        </SideBarItem>
      )}
      <FolderInfo>
        <div>
          <FolderNameLabel>{t('common:label.folder')}</FolderNameLabel>
          <FolderNameText>{folderName}</FolderNameText>
        </div>
        <FolderTalentCount>{t('common:count.talent', { count: talentCount })}</FolderTalentCount>
        <StyledChooseTalentButton outlined type="button" role="button" onClick={handleSeeAllTalentModalOpen}>
          {t('common:button.chooseTalent')}
        </StyledChooseTalentButton>
        {talentCount > 1 && Boolean(currentTalentPosition) && (
          <StyledDirectionButtonGroup>
            <StyledDirectionButton
              role="button"
              color="link"
              disabled={_isNil(prevTalentGuid)}
              value={prevTalentGuid}
              onClick={handleTalentChange}
            >
              {`< ${t('common:button.prevTalent')}`}
            </StyledDirectionButton>
            {t('common:count.currentAndMaximum', { current: currentTalentPosition, maximum: talentCount })}
            <StyledDirectionButton
              role="button"
              color="link"
              disabled={_isNil(nextTalentGuid)}
              value={nextTalentGuid}
              onClick={handleTalentChange}
            >
              {t('common:button.nextTalentWithArrow')}
            </StyledDirectionButton>
          </StyledDirectionButtonGroup>
        )}
        <TalentList>
          {_map(talentList, (talent) => (
            <StyledTalentCardLink key={talent.id} to={`${gridViewLinkPath}/${talent?.guid || talent?.profile?.guid}`}>
              <StyledTalentCard
                isPublic
                $isActive={talent?.guid === currentTalentGuid || talent?.profile?.guid === currentTalentGuid}
                appType={appType}
                skipRepsShortCodes={false}
                submission={{
                  id: talent?.submissionId || talent.id,
                  cover: talent?.cover,
                  profile: {
                    ...(talent?.profile || talent),
                    profileId: talent?.profile?.id || talent.id,
                  },
                  role: {
                    name: talent.roleName,
                  },
                  contactInfo: talent?.contactInfo || talent?.profile?.contactInfo,
                }}
                tooltipPlacement="top-end"
                showPickButton={!isShareSelectsPresentation}
                userName={userName}
                handleUserNameModalToggle={userNameModalToggle}
                presentationId={presentationId}
                guid={presentationGuid}
                pickedBy={talent.pickedBy}
                pickCount={talent.pickCount}
              />
            </StyledTalentCardLink>
          ))}
          {hasNextPage && <LoaderWrapper>{isNextPageLoading && <Loader />}</LoaderWrapper>}
        </TalentList>
      </FolderInfo>
    </Wrapper>
  );
};

export default RecipientDetailViewSideBar;
